import {getDisplayName} from "../../resources/users";
import {each, filter, groupBy, isArray, isEmpty, map, mapObject, some, sortBy} from "underscore";

export const AGENT_EDITABLE_ACTION_FIELDS = ["owner", "phase", "roles"];

export const isActionInFuture = ({action, incident}) =>
  action?.relevantIncidentPhase?.ordering &&
  incident?.phase?.ordering &&
  action.relevantIncidentPhase.ordering > incident.phase.ordering;

export const getSelectedActionFromScope = (scopeContext) => {
  if (isEmpty(scopeContext)) {
    return null;
  }

  const source = scopeContext.isMyActions ? scopeContext : scopeContext.subPageContext;

  if (!source?.id) {
    return null;
  }

  return {
    id: source.id,
    name: source.name,
    phase: source.phase,
    actionType: source.actionType,
    availableControls: source.availableControls,
    activeControl: source.activeControl,
  };
};

export const groupingOptions = [
  {
    optionValue: "relevantIncidentPhase",
    optionText: "Incident Phase",
    tooltip: "Groups by the incident phase",
  },
  {
    optionValue: "user",
    optionText: "User",
    tooltip: "Groups by the assigned user",
  },
  {
    optionValue: "role",
    optionText: "Roles",
    tooltip: "Groups by assigned roles",
  },
  {
    optionValue: "myRoles",
    optionText: "My Roles",
    tooltip: "Groups by whether it contains any of your roles",
  },
  {
    optionValue: "obligation",
    optionText: "Procedures",
    tooltip: "Groups by the procedures",
  },
  {
    optionValue: "phase",
    optionText: "Action Phase",
    tooltip: "Groups by the current phase",
  },
  {
    optionValue: "actionType",
    optionText: "Action Type",
    tooltip: "Groups by the action type",
  },
  {
    optionValue: "taskType",
    optionText: "Task Type",
    tooltip: "Groups by the task type",
  },
  {
    optionValue: "deadlineAt",
    optionText: "Has Deadline",
    tooltip: "Groups by whether it has a deadline",
  },
  {
    optionValue: "recurrence",
    optionText: "Recurring",
    tooltip: "Groups by whether it is recurring",
  },
  {
    optionValue: "none",
    optionText: "No Grouping",
    tooltip: "Show all as one group",
  },
];

export const sortingOptions = [
  {
    optionValue: "default",
    optionText: "Default",
    tooltip: "Default sorting order within groups, prioritizing reordering",
  },
  {
    optionValue: "updatedAt",
    optionText: "Stale First",
    tooltip: "Shows the least recently updated at the top of each group",
  },
  {
    optionValue: "createdAt",
    optionText: "Time Created",
    tooltip: "Oldest created will appear at the top of each group",
  },
];

export const actionTaskSortByNameAndOrdering = (tasks = []) => {
  if (!isArray(tasks)) {
    return [];
  }

  return [...tasks].sort((a, b) => {
    const aIncidentPhaseOrdering = a?.incidentPhaseOrdering ?? Infinity;
    const bIncidentPhaseOrdering = b?.incidentPhaseOrdering ?? Infinity;
    if (aIncidentPhaseOrdering !== bIncidentPhaseOrdering) {
      return aIncidentPhaseOrdering - bIncidentPhaseOrdering;
    }

    const aOrdering =
      a?.task?.relevantIncidentPhase?.ordering ?? a?.relevantIncidentPhase?.ordering ?? Infinity;
    const bOrdering =
      b?.task?.relevantIncidentPhase?.ordering ?? b?.relevantIncidentPhase?.ordering ?? Infinity;
    if (aOrdering !== bOrdering) {
      return aOrdering - bOrdering;
    }

    const aPhaseOrdering = a?.phaseOrdering ?? Infinity;
    const bPhaseOrdering = b?.phaseOrdering ?? Infinity;
    if (aPhaseOrdering !== bPhaseOrdering) {
      return aPhaseOrdering - bPhaseOrdering;
    }

    const aName = `${a?.task?.name ?? a?.name}`.toLocaleLowerCase();
    const bName = `${b?.task?.name ?? b?.name}`.toLocaleLowerCase();
    if (aName !== bName) {
      return aName > bName ? 1 : -1;
    }

    return (a?.id ?? 0) - (b?.id ?? 0);
  });
};

export const groupActionsByVisibility = ({actions, incident, activeActionVisibility}) => {
  if (!isArray(actions)) {
    return {};
  }

  const sortedActions = actionTaskSortByNameAndOrdering(actions);

  if (incident && activeActionVisibility === "Restricted") {
    const visibleActions = [];
    const hiddenActions = [];
    each(sortedActions, (action) => {
      if (!isActionInFuture({action, incident})) {
        visibleActions.push(action);
      } else {
        hiddenActions.push(action);
      }
    });
    return {actions: sortedActions, visibleActions, hiddenActions};
  }
  return {
    actions: sortedActions,
    visibleActions: sortedActions,
    hiddenActions: [],
  };
};

export const orderAndGroupVisibleActions = ({actions, incident, activeActionVisibility}) => {
  const visibleActions = groupActionsByVisibility({
    actions,
    incident,
    activeActionVisibility,
  }).visibleActions;

  const groupedActions = groupBy(
    sortBy(visibleActions, (action) => action?.relevantIncidentPhase?.ordering),
    (action) => {
      return action?.relevantIncidentPhase.name;
    },
  );

  return groupedActions;
};

export const isActionLimited = ({action, activeActionVisibility, incident}) =>
  incident && activeActionVisibility === "Limited" && isActionInFuture({action, incident});

export const isActionInLastActionPhase = (selectedAction, lastActionPhase) =>
  selectedAction?.phase?.id &&
  lastActionPhase?.id &&
  selectedAction.phase.id === lastActionPhase.id;

export const getGroupedActions = (filteredActions, groupedBy, user) => {
  const scenarioActions = filter(
    filteredActions,
    (action) => action?.relevantIncidentPhase?.ordering < 0,
  );

  const incidentActions = filter(
    filteredActions,
    (action) =>
      !action?.relevantIncidentPhase?.ordering || action?.relevantIncidentPhase?.ordering >= 0,
  );

  let storedIdsForGrouping;
  let sortedIncidentActions;
  if (["phase"].includes(groupedBy)) {
    sortedIncidentActions = sortBy(incidentActions, (action) => action.phase.id);
  } else if (["user"].includes(groupedBy)) {
    sortedIncidentActions = sortBy(incidentActions, (action) =>
      isEmpty(action.user) ? 1 : action.user.id === user.id ? 0 : 2,
    );
  } else if (["myRoles"].includes(groupedBy)) {
    storedIdsForGrouping = map(user?.roles, (role) => role.roleId);
    sortedIncidentActions = sortBy(incidentActions, (action) =>
      some(action.roles, (role) => storedIdsForGrouping.includes(role.roleId)) ? 0 : 1,
    );
  } else if (["recurrence", "deadlineAt"].includes(groupedBy)) {
    sortedIncidentActions = sortBy(incidentActions, (action) =>
      !isEmpty(action[groupedBy]) ? 0 : 1,
    );
  } else {
    sortedIncidentActions = incidentActions;
  }

  const groupedActions = groupBy(sortedIncidentActions, (action) => {
    if (["relevantIncidentPhase", "phase"].includes(groupedBy)) {
      return action?.[groupedBy].name;
    }

    if (["user"].includes(groupedBy)) {
      if (isEmpty(action.user)) {
        return "No User Assigned";
      }
      return getDisplayName(action?.user);
    }

    if (["role", "obligation"].includes(groupedBy)) {
      if (isEmpty(action?.[`${groupedBy}s`])) {
        return `No ${groupedBy}`;
      }
      const listToGroupFrom = sortBy(action?.[`${groupedBy}s`], (item) => item[groupedBy].id);
      const combinedNames = map(listToGroupFrom, (item) => item[groupedBy].name).join(", ");
      return combinedNames;
    }

    if (["actionType"].includes(groupedBy)) {
      return action?.[groupedBy];
    }

    if (["taskType"].includes(groupedBy)) {
      return action?.task?.type?.name ?? "No Task Type";
    }

    if (["myRoles"].includes(groupedBy)) {
      return some(action.roles, (role) => storedIdsForGrouping.includes(role.roleId)) ?
        "Contains My Role" :
        "None of My Roles";
    }

    if (["recurrence", "deadlineAt"].includes(groupedBy)) {
      const labelFirstWord = isEmpty(action[groupedBy]) ? "No" : "Has";
      return `${labelFirstWord} ${groupedBy === "deadlineAt" ? "deadline" : groupedBy}`;
    }
    if (["none"].includes(groupedBy)) {
      return "All Actions";
    }
  });

  const scenarioGroupedActions = groupBy(
    scenarioActions,
    (action) => action?.relevantIncidentPhase?.name,
  );

  return {...scenarioGroupedActions, ...groupedActions};
};

export const sortActionsWithinGroup = (groupedActions, sortedBy) => {
  if ("default" === sortedBy) {
    return groupedActions;
  }
  if (["updatedAt", "createdAt"].includes(sortedBy)) {
    const sortedByDateTimeValue = mapObject(groupedActions, (groupOfActions) => {
      if (groupOfActions?.[0]?.relevantIncidentPhase?.ordering < 0) {
        return groupOfActions;
      }
      return sortBy(groupOfActions, (action) => action[sortedBy]);
    });
    return sortedByDateTimeValue;
  }
  return groupedActions;
};

export const getDescendantCount = (allActions, parentActionId) => {
  if (!allActions || !parentActionId) {
    return 0;
  }
  return filter(allActions, (action) => action?.recurrence?.parentActionId === parentActionId)
    .length;
};
